import { Color } from "../../../../core/models/entities/LogoOptions";
import { ColorDto } from "../../../../data/models/responses/logoOptionsDto";
import { IdLookUps } from "../../../../utils/models/idLookUp";

/**
 * Flag REACT_APP_LOGO_EDITOR_ALL_CURRENT_COLORS_ENABLED (20/10, item 1103001
 * «logo-hotscreen», PMS 3546 C): the colour changer's lookup for the logo's
 * CURRENT colours, filled so every code in logo.colors resolves to a row.
 *
 * Precedence, first hit wins:
 *   1. `lookup` - today's table, built from the method's change-colour list
 *      (options.colors / options.colorsEmbroidery). Untouched.
 *   2. `allColors` - the supplier's full list (options.allColors), which
 *      holds the colours the API does NOT mark IsChangeColor.
 *   3. `colorDetails` - the logo's own colour rows from GetLogo. They are
 *      ColorDto, which is the same shape as Color: the mapper's toColor is
 *      the identity (mapLogoOptions.mapToColor), and mapLogo passes
 *      colorDetails through as is - so a row is taken verbatim here too.
 *
 * Pure: reads no flag, no colour methods, makes no call. Returns a NEW
 * object; the input lookup is never mutated. A code known nowhere stays
 * absent (the caller filters, exactly as today).
 */
export function extendCurrentColorLookup(
  lookup: IdLookUps<Color>,
  logoColors: string[] | null | undefined,
  allColors: Color[] | null | undefined,
  colorDetails: ColorDto[] | null | undefined,
): IdLookUps<Color> {
  const missing = (logoColors ?? []).filter((code) => !lookup[code]);
  if (missing.length === 0) return { ...lookup };

  const fromAllColors = allColors ?? [];
  const fromDetails = colorDetails ?? [];
  const found = missing.flatMap((code): [string, Color][] => {
    const color =
      fromAllColors.find((c) => c.id === code) ??
      fromDetails.find((c) => c.id === code);
    return color ? [[code, color]] : [];
  });

  return { ...lookup, ...Object.fromEntries(found) };
}
